import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Badge,
  Table,
  TableRow,
  TableCell,
  TablePagination,
  Tooltip,
  TableProvider,
  TableBody,
  SearchInput,
  TableSearchAndFilters
} from '@percent/lemonade'
import { useAuthState, useQuery, useSearchFilter } from '@percent/partner-dashboard/common/hooks'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import { ErrorView } from '@percent/partner-dashboard/common/components'
import { RemoveUserModal } from './modals/RemoveUserModal'
import { EditUserModal } from './modals/EditUserModal'
import styles from './UsersTable.module.scss'
import { EditUserModalProps } from './modals/EditUserModal.types'
import { RemoveUserModalProps } from './modals/RemoveUserModal.types'
import { UsersTableProps } from './UsersTable.types'
import { UsersActionsMenu } from './usersActionMenu/UsersActionsMenu'

export function UsersTable({
  data,
  isLoading,
  totalResults,
  previousPage,
  nextPage,
  refresh,
  queryParams,
  setQueryParams
}: Readonly<UsersTableProps>) {
  const { t } = useTranslation()
  const { user: currentUser } = useAuthState()
  const { iamService } = useServices()
  const [editModalData, setEditModalData] = useState<EditUserModalProps['openModalData']>(undefined)
  const [removeModalData, setRemoveModalData] = useState<RemoveUserModalProps['openModalData']>(undefined)

  const [{ body: assignableRolesBody, errorMessage: assignableRolesError }] = useQuery(
    iamService.getAssignableRoles,
    {}
  )
  const assignableRoles = useMemo(() => assignableRolesBody?.data?.roles ?? [], [assignableRolesBody])
  const assignableRoleIds = useMemo(() => new Set(assignableRoles.map(r => r.id)), [assignableRoles])
  const canEditAnyone = assignableRoleIds.size > 0

  const { searchValue, handleChange, handleKeyPress, handleClearValue } = useSearchFilter({
    queryParams: { query: queryParams?.search },
    setQueryParams: params => setQueryParams?.({ search: params.query })
  })

  const isEditable = (idOfEditedUser: string, roleIdOfEditedUser: string) => {
    if (!currentUser || currentUser.id === idOfEditedUser) {
      return false
    }

    return assignableRoleIds.has(roleIdOfEditedUser)
  }

  const columnsUsers = [
    { id: t('table.header.email'), isSortable: false },
    { id: t('table.header.role'), isSortable: false },
    { id: t('table.header.2fa'), isSortable: false },
    ...(canEditAnyone ? [{ id: '', isSortable: false }] : [])
  ]

  const mapTooltip = (name: string) => {
    const tooltip = {
      Owner: t('toolTip.myTeam.owner'),
      Admin: t('toolTip.myTeam.admin'),
      Developer: t('toolTip.myTeam.developer'),
      Member: t('toolTip.myTeam.member')
    }

    return tooltip[name]
  }

  const activeSearch = queryParams?.search

  if (assignableRolesError) {
    return <ErrorView errorMessage={assignableRolesError} />
  }

  return (
    <TableProvider
      columns={columnsUsers}
      totalResults={totalResults || 0}
      isLoading={isLoading || !data}
      illustration="no-results"
      emptyTableTitle={
        activeSearch
          ? t('typography.noSearchTableListTitle', { searchTerm: activeSearch })
          : t('typography.noListTitle', { listType: 'users' })
      }
      emptyTableDescription={
        activeSearch
          ? t('typography.noSearchTableListDescription')
          : t('typography.noListDescription', { listType: 'users' })
      }
    >
      <>
        <TableSearchAndFilters>
          <SearchInput
            name="search"
            value={searchValue}
            onKeyDown={handleKeyPress}
            onChange={handleChange}
            handleClearValue={handleClearValue}
            placeholder={t('table.usersListSearchPlaceholder')}
            status={searchValue.length > 255 ? 'danger' : 'default'}
            statusMessage={t('errorMessage.shouldNotExceed255')}
          />
        </TableSearchAndFilters>
        <Table data-testid="users-table">
          <TableBody>
            {data?.map(({ id, email, role, mfaEnabled, status }, _index) => (
              <TableRow key={`permissions-list-${id}`} data-testid={`account-${id}`}>
                <TableCell>
                  <div className={styles.cellWrapper}>
                    <span className={status === 'removed' ? styles.disabled : undefined}>{email}</span>
                    {status === 'pending' ? (
                      <Badge variant="default" icon="clock">
                        {t('table.accounts.status.pending')}
                      </Badge>
                    ) : status === 'removed' ? (
                      <Badge variant="default" icon="reject">
                        {t('table.accounts.status.removed')}
                      </Badge>
                    ) : null}
                  </div>
                </TableCell>
                <TableCell>
                  {role && (
                    <Tooltip content={mapTooltip(role.name)}>
                      <Badge variant="default" testId="role-badge">
                        {role.name}
                      </Badge>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  {mfaEnabled ? (
                    <Badge icon="check-badge" variant="positive">
                      {t('2fa.enable')}
                    </Badge>
                  ) : (
                    <Badge icon="warning" variant="critical">
                      {t('2fa.disable')}
                    </Badge>
                  )}
                </TableCell>
                <TableCell className={styles.selectCell} data-testid="actions-table-cell">
                  {role && isEditable(id, role.id) && (
                    <UsersActionsMenu
                      items={[{ key: 'edit', label: t('button.edit') }]}
                      handleSelect={(actionKey: string) => {
                        if (actionKey === 'edit') {
                          setEditModalData({ userId: id, email, role: role.name, roleId: role.id })
                        }
                      }}
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          totalResultsText={t('table.results', { count: totalResults || undefined })}
          previousPage={previousPage}
          nextPage={nextPage}
        />
        <EditUserModal
          refresh={refresh}
          openModalData={editModalData}
          assignableRoles={assignableRoles}
          onClose={() => {
            setEditModalData(undefined)
          }}
        />
        <RemoveUserModal
          refresh={refresh}
          openModalData={removeModalData}
          onClose={() => {
            setRemoveModalData(undefined)
          }}
        />
      </>
    </TableProvider>
  )
}
